/**
 * Correcting a bank account nobody has agreed to yet.
 *
 * Offered on a draft, and on an account a Finance Head sent back. "Sent back
 * for correction" promised a correction, and without this there was no way to
 * make one: the account returned to its maker, who could read the reason and
 * do nothing about it but send the same details again.
 *
 * It is not offered once the account is submitted or approved. A submitted
 * account is with a checker and is not edited under them; an approved one is
 * approved for exactly these details, and the server refuses both.
 *
 * The account number is the one field that starts empty. Only its masked form
 * ever reaches this screen, so there is nothing to pre-fill -- leaving it
 * blank keeps the number that is stored, and typing one replaces it.
 */

import { useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '../api'
import { Card, ErrorNote, Field, Note } from '../components/ui'
import { IFSC_SHAPE, KINDS_AT_A_BRANCH } from './AddBankAccount'

export interface EditableAccount {
  id: string
  name: string
  bank_name: string
  account_number_masked: string
  ifsc: string | null
  kind: string
  status: string
}

export default function EditBankAccount({
  account,
  onClose,
}: {
  account: EditableAccount
  onClose: () => void
}) {
  const queryClient = useQueryClient()
  const [name, setName] = useState(account.name)
  const [bankName, setBankName] = useState(account.bank_name)
  const [accountNumber, setAccountNumber] = useState('')
  const [ifsc, setIfsc] = useState(account.ifsc ?? '')

  const needsIfsc = KINDS_AT_A_BRANCH.has(account.kind)
  const ifscTyped = ifsc.trim()
  const ifscLooksRight = IFSC_SHAPE.test(ifscTyped)

  //: Only what was actually changed. The server leaves alone anything it is
  //: not sent, so an untouched account number is never rewritten -- which is
  //: what makes it safe for this form not to know the number in the first
  //: place.
  const changed: Record<string, string | null> = {}
  if (name.trim() !== account.name) changed.name = name.trim()
  if (bankName.trim() !== account.bank_name) changed.bank_name = bankName.trim()
  if (accountNumber.trim()) changed.account_number = accountNumber.trim()
  if (ifscTyped !== (account.ifsc ?? '')) changed.ifsc = ifscTyped || null

  const save = useMutation({
    mutationFn: () => api.patch(`/banking/accounts/${account.id}`, changed),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['bank-accounts'] })
      onClose()
    },
  })

  const ready =
    name.trim() &&
    bankName.trim() &&
    Object.keys(changed).length > 0 &&
    (needsIfsc ? ifscLooksRight : !ifscTyped || ifscLooksRight)

  return (
    <Card
      title={`Correct ${account.name}`}
      hint={
        account.status === 'returned'
          ? 'sent back by a Finance Head — fix it, then send it for approval again'
          : 'still a draft — nobody has been asked to approve it yet'
      }
    >
      <ErrorNote error={save.error} />
      <form
        onSubmit={(e) => {
          e.preventDefault()
          if (ready) save.mutate()
        }}
      >
        <div className="row">
          <Field label="What you call it">
            <input value={name} onChange={(e) => setName(e.target.value)} required />
          </Field>
          <Field label="Bank">
            <input value={bankName} onChange={(e) => setBankName(e.target.value)} required />
          </Field>
          <Field
            label="Account number"
            caption={`leave blank to keep ${account.account_number_masked}`}
          >
            <input
              className="mono"
              value={accountNumber}
              onChange={(e) => setAccountNumber(e.target.value)}
              placeholder={account.account_number_masked}
            />
          </Field>
          <Field
            label="IFSC"
            caption={needsIfsc ? 'the branch code, as printed on the cheque book' : 'optional'}
          >
            <input
              className="mono"
              value={ifsc}
              onChange={(e) => setIfsc(e.target.value.toUpperCase().replace(/\s/g, ''))}
              placeholder="SBIN0015781"
              maxLength={11}
              required={needsIfsc}
            />
          </Field>
        </div>

        {ifscTyped && !ifscLooksRight && (
          <Note kind="warn">
            An IFSC is eleven characters: four letters for the bank, a zero, then six
            letters or digits for the branch — <span className="mono">SBIN0015781</span>,
            for example.
          </Note>
        )}

        <div className="actions">
          <button className="primary" type="submit" disabled={!ready || save.isPending}>
            {save.isPending ? 'Saving…' : 'Save the correction'}
          </button>
          <button type="button" className="quiet" onClick={onClose}>
            Cancel
          </button>
        </div>
      </form>
    </Card>
  )
}
